import { Injectable } from '@angular/core';
import { GalleryFiltersAndSorting } from '@app/entities/images/interfaces/gallery-filters-and-sorting';
import { ImagePreview } from '@app/entities/images/interfaces/image-preview';
import { PaginationResultsWithTags } from '@app/entities/images/interfaces/pagination-results-with-tags';
import { ImagesService } from '@app/entities/images/services/images.service';
import { contenTypeFilterOptions } from '@app/pages/gallery/content-type-filter/content-type-filter-options';
import { ContentTypeFilterValues } from '@app/pages/gallery/content-type-filter/content-type-filter-values';
import { OrientationFilterValues } from '@app/pages/gallery/orientation-filter/orientation-filter-values';
import { PricingFilterValues } from '@app/pages/gallery/pricing-filter/pricing-filter-values';
import { SortingOptionsValues } from '@app/pages/gallery/sorting-options/sorting-options-values';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { AddToFavorites, RemoveFromFavorites } from '../image';
import { GalleryStateModel } from './gallery-state.model';
import {
  LoadGallery,
  ResetGalleryFilters,
  ResetGallerySecondaryFilters,
  SetContentTypeFilterValue,
  SetGalleryCategory,
  SetOrientationFilterValue,
  SetPricingFilterValue,
  SetSearchRequestValue,
  SetSortingOptionsValue,
  ToggleContentTypeFilterValue,
} from './gallery.actions';

@State<GalleryStateModel>({
  name: 'gallery',
  defaults: {
    tags: [],
    searchRequest: '',
    isFree: PricingFilterValues.ALL,
    orientation: OrientationFilterValues.ALL,
    ordering: SortingOptionsValues.LATEST,
    type: [],
    page: 1,
  },
})
@Injectable()
export class GalleryState {
  constructor(private imagesService: ImagesService) {}

  @Action(LoadGallery, { cancelUncompleted: true })
  @ImmutableContext()
  loadGallery(
    { getState, setState }: StateContext<GalleryStateModel>,
    { preserveResults }: LoadGallery,
  ) {
    const filters: GalleryFiltersAndSorting = {};

    const searchRequest = getState().searchRequest;
    if (searchRequest !== '') {
      filters.search = searchRequest;
    }

    const category = getState().category;
    if (category) {
      filters.category = category?.toString();
    }

    const isFree = getState().isFree;
    if (isFree !== PricingFilterValues.ALL) {
      filters.is_free = isFree;
    }

    const orientation = getState().orientation;
    if (orientation !== OrientationFilterValues.ALL) {
      filters.orientation = orientation;
    }

    const ordering = getState().ordering;
    if (ordering !== SortingOptionsValues.LATEST) {
      filters.ordering = ordering;
    }

    const type = getState().type;
    if (type.length > 0) {
      const predefinedOrder = Array.from(contenTypeFilterOptions.keys());
      const sortedType = type
        .slice()
        .sort(
          (a, b) => predefinedOrder.indexOf(a) - predefinedOrder.indexOf(b),
        );
      filters.type = sortedType.join(',');
    }

    const loadPage = preserveResults ? getState().page + 1 : 1;
    return this.imagesService.getGallery(filters, loadPage).pipe(
      tap((paginatedResults: PaginationResultsWithTags<ImagePreview>) => {
        return setState((state: GalleryStateModel) => {
          state.page = loadPage;
          state.images = preserveResults
            ? [...(state.images || []), ...paginatedResults.results]
            : paginatedResults.results;
          state.totalCount = paginatedResults.count;
          if (loadPage === 1) {
            state.tags = paginatedResults.tags || [];
          }

          return state;
        });
      }),
    );
  }

  @Action(AddToFavorites)
  @ImmutableContext()
  updateFavoriteStatus(
    { setState }: StateContext<GalleryStateModel>,
    { imageId }: AddToFavorites,
  ) {
    setState((state: GalleryStateModel) => {
      const image = state.images?.find(
        (image: ImagePreview) => image.id === imageId,
      );

      if (image) {
        image.is_favorite = true;
      }

      return state;
    });
  }

  @Action(RemoveFromFavorites)
  @ImmutableContext()
  removeFromFavorites(
    { setState }: StateContext<GalleryStateModel>,
    { imageId }: RemoveFromFavorites,
  ) {
    setState((state: GalleryStateModel) => {
      const image = state.images?.find(
        (image: ImagePreview) => image.id === imageId,
      );

      if (image) {
        image.is_favorite = false;
      }

      return state;
    });
  }

  @Action(SetSearchRequestValue)
  @ImmutableContext()
  setSearchRequestValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetSearchRequestValue,
  ) {
    return setState((state: GalleryStateModel) => {
      state.searchRequest = value;

      return state;
    });
  }

  @Action(SetGalleryCategory)
  @ImmutableContext()
  setGalleryCategory(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetGalleryCategory,
  ) {
    return setState((state: GalleryStateModel) => {
      state.category = value;

      return state;
    });
  }

  @Action(SetPricingFilterValue)
  @ImmutableContext()
  setPricingFilterValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetPricingFilterValue,
  ) {
    return setState((state: GalleryStateModel) => {
      state.isFree = value;

      return state;
    });
  }

  @Action(SetContentTypeFilterValue)
  @ImmutableContext()
  setContentTypeFilterValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetContentTypeFilterValue,
  ) {
    const isContentTypeFilterValue = (
      value: string,
    ): value is ContentTypeFilterValues => {
      return Object.values(ContentTypeFilterValues).includes(
        value as ContentTypeFilterValues,
      );
    };

    return setState((state: GalleryStateModel) => {
      const types: ContentTypeFilterValues[] = value
        .split(',')
        .filter((type: string) => {
          return isContentTypeFilterValue(type);
        });

      state.type = types;

      return state;
    });
  }

  @Action(ToggleContentTypeFilterValue)
  @ImmutableContext()
  toggleContentTypeFilterValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: ToggleContentTypeFilterValue,
  ) {
    return setState((state: GalleryStateModel) => {
      if (state.type.includes(value)) {
        state.type = state.type.filter((type: ContentTypeFilterValues) => {
          return type !== value;
        });
      } else {
        state.type.push(value);
      }

      return state;
    });
  }

  @Action(SetOrientationFilterValue)
  @ImmutableContext()
  setOrientationFilterValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetOrientationFilterValue,
  ) {
    return setState((state: GalleryStateModel) => {
      state.orientation = value;

      return state;
    });
  }

  @Action(SetSortingOptionsValue)
  @ImmutableContext()
  setSortingOptionsValue(
    { setState }: StateContext<GalleryStateModel>,
    { value }: SetSortingOptionsValue,
  ) {
    return setState((state: GalleryStateModel) => {
      state.ordering = value;

      return state;
    });
  }

  @Action(ResetGalleryFilters)
  @ImmutableContext()
  resetGalleryFilters({ setState }: StateContext<GalleryStateModel>) {
    return setState((state: GalleryStateModel) => {
      state.searchRequest = '';
      delete state.category;
      state.isFree = PricingFilterValues.ALL;
      state.orientation = OrientationFilterValues.ALL;
      // state.ordering = SortingOptionsValues.LATEST;
      state.type = [];
      return state;
    });
  }

  @Action(ResetGallerySecondaryFilters)
  @ImmutableContext()
  resetGallerySecondaryFilters({ setState }: StateContext<GalleryStateModel>) {
    return setState((state: GalleryStateModel) => {
      state.isFree = PricingFilterValues.ALL;
      state.orientation = OrientationFilterValues.ALL;
      // state.ordering = SortingOptionsValues.LATEST;
      state.type = [];
      return state;
    });
  }
}
